import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Search, ArrowRight, Filter } from 'lucide-react';
import { getHeroImage } from '../utils/heroImages';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ArticleCard = ({ article }) => (
  <Link
    to={`/article/${article.slug || article.id}`}
    className="group bg-white rounded-xl shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden"
    data-testid={`article-${article.slug || article.id}`}
  >
    <img src={getHeroImage(article.category)} alt={article.title} className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
    <div className="p-6">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-xs font-semibold text-blue-600 uppercase tracking-wide">{article.category.replace(/-/g, ' ')}</span>
        <span className="text-gray-400">&bull;</span>
        <span className="text-xs text-gray-500">{article.read_time} min read</span>
      </div>
      <h3 className="text-xl font-bold mb-3 text-gray-900 group-hover:text-blue-600 transition-colors line-clamp-2">{article.title}</h3>
      <p className="text-gray-600 mb-4 line-clamp-3">{article.excerpt}</p>
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-500">{article.author}</span>
        <ArrowRight className="text-blue-600 group-hover:translate-x-1 transition-transform" size={20} />
      </div>
      {article.featured && (
        <div className="mt-3">
          <span className="inline-block px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded">Featured</span>
        </div>
      )}
    </div>
  </Link>
);

const ArticleResults = ({ loading, articles }) => {
  if (loading) {
    return <div className="text-center py-20"><div className="text-xl text-gray-600">Loading articles...</div></div>;
  }
  if (articles.length === 0) {
    return <div className="text-center py-20"><div className="text-xl text-gray-600">No articles found. Try a different search or category.</div></div>;
  }
  return (
    <div>
      <div className="mb-6 text-gray-600">Showing {articles.length} article{articles.length !== 1 ? 's' : ''}</div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="articles-grid">
        {articles.map((article) => <ArticleCard key={article.id} article={article} />)}
      </div>
    </div>
  );
};

const ResearchHub = () => {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  const loadCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error loading categories:', error);
    }
  }, []);

  const loadArticles = useCallback(async () => {
    setLoading(true);
    try {
      const url = selectedCategory === 'all'
        ? `${API}/articles?limit=100`
        : `${API}/articles?category=${selectedCategory}&limit=100`;
      const response = await axios.get(url);
      setArticles(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error loading articles:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    loadArticles();
  }, [loadArticles]);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (searchQuery.trim().length < 2) {
      loadArticles();
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API}/search?q=${encodeURIComponent(searchQuery)}`);
      setArticles(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error searching:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>{'Peptide Research Library | 64+ Evidence-Based Articles | PeptideSciences.ai'}</title>
        <meta name="description" content="Browse 64+ peer-reviewed peptide research articles across 32 health categories. Topics include BPC-157, GLP-1, TB-500, anti-aging, weight loss, brain health, and more." />
        <link rel="canonical" href="https://www.peptidesciences.ai/research" />
        <meta property="og:title" content="Peptide Research Library | PeptideSciences.ai" />
        <meta property="og:description" content="Browse 64+ evidence-based peptide research articles across 32 health categories." />
        <meta property="og:url" content="https://www.peptidesciences.ai/research" />
      </Helmet>
      <Navigation />

      <section className="bg-gradient-to-r from-gray-900 via-slate-800 to-gray-900 text-white py-16 px-4 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{backgroundImage: 'url(https://customer-assets.emergentagent.com/job_07859234-c0aa-4e8d-94c2-c8d61ccd3354/artifacts/a25q64nh_Peptide%20Background.png)', backgroundSize: 'cover', backgroundPosition: 'center'}}></div>
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-slate-800/85 to-gray-900/90"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <h1 className="text-5xl font-bold mb-4" data-testid="research-hub-title">Research Library</h1>
          <p className="text-lg text-gray-300">Comprehensive peptide research across all categories</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="mb-12">
          <form onSubmit={handleSearch} className="mb-6">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input type="text" placeholder="Search articles, topics, peptides..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-testid="search-input" />
              </div>
              <button type="submit" className="px-8 py-4 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-all" data-testid="search-button">Search</button>
            </div>
          </form>

          <div className="flex items-center gap-4 mb-4">
            <Filter size={20} className="text-gray-600" />
            <span className="font-semibold text-gray-700">Filter by Category:</span>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="category-filters">
            <button onClick={() => setSelectedCategory('all')} className={`px-4 py-2 rounded-full font-medium transition-all ${selectedCategory === 'all' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'}`} data-testid="filter-all">All Categories</button>
            {categories.map((category) => (
              <button key={category.slug} onClick={() => setSelectedCategory(category.slug)} className={`px-4 py-2 rounded-full font-medium transition-all ${selectedCategory === category.slug ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'}`} data-testid={`filter-${category.slug}`}>{category.name}</button>
            ))}
          </div>
        </div>

        <ArticleResults loading={loading} articles={articles} />
      </div>

      <Footer />
    </div>
  );
};

export default ResearchHub;
